import { usePostHog } from "@posthog/react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowRight, Loader2, Lock, Mail } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { LoadingScreen } from "@/components/LoadingScreen";
import { PublicFooter, PublicHeader } from "@/components/public-layout";
import { useWebHaptics } from "@/lib/haptics";
import { noIndexMeta } from "@/lib/seo";
import { useSession } from "../hooks/auth-hooks";
import { signIn } from "../lib/auth-client";

export const Route = createFileRoute("/login")({
	component: LoginPage,
	head: () => ({
		meta: [{ title: "AgentReacher | Log in" }, ...noIndexMeta()],
	}),
});

const authGridCells = Array.from({ length: 44 }, (_, index) => ({
	id: `auth-grid-cell-${index}`,
	isHiddenOnMobile: index >= 8,
}));

function LoginPage() {
	const posthog = usePostHog();
	const { trigger } = useWebHaptics();
	const { session, user, isPending, refetch } = useSession();
	const navigate = useNavigate();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const emailId = useId();
	const passwordId = useId();

	const redirectParam =
		typeof window !== "undefined"
			? new URLSearchParams(window.location.search).get("redirect")
			: null;

	const handleEmailLogin = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		setLoading(true);

		try {
			const result = await signIn.email({
				email,
				password,
			});
			if (result.error) {
				setError(result.error.message || "Failed to sign in");
			} else {
				posthog?.capture("user_signed_in", { method: "email" });
				trigger("success");
				await refetch();
				if (redirectParam) {
					window.location.assign(redirectParam);
				} else {
					navigate({ to: "/dashboard" });
				}
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : "An error occurred");
		} finally {
			setLoading(false);
		}
	};

	const handleGoogleSignIn = async () => {
		setError(null);
		trigger("success");
		try {
			await signIn.social({
				provider: "google",
				callbackURL: redirectParam || "/dashboard",
			});
		} catch (err) {
			setError(
				err instanceof Error ? err.message : "Failed to sign in with Google",
			);
		}
	};

	const shouldRedirectToDashboard = !isPending && !!session && !!user;

	useEffect(() => {
		if (!shouldRedirectToDashboard) return;
		if (redirectParam) {
			window.location.assign(redirectParam);
		} else {
			navigate({ to: "/dashboard", replace: true });
		}
	}, [navigate, redirectParam, shouldRedirectToDashboard]);

	// Prevent UI flash while redirecting
	if (shouldRedirectToDashboard) return null;

	if (isPending) {
		return <LoadingScreen />;
	}

	return (
		<div className="flex min-h-svh flex-col bg-background text-foreground">
			<PublicHeader />
			<div className="mx-auto flex w-full max-w-6xl flex-1 px-4 sm:px-6">
				<div className="relative flex flex-1 flex-col overflow-hidden bg-border px-px pb-px [--auth-row-size:4.75rem] sm:[--auth-row-size:5.25rem]">
					<main className="relative grid flex-1 grid-cols-4 grid-rows-[minmax(var(--auth-row-size),1fr)_repeat(7,minmax(var(--auth-row-size),auto))_minmax(var(--auth-row-size),1fr)] gap-px sm:grid-cols-8">
						{authGridCells.map((cell) => (
							<div
								key={cell.id}
								aria-hidden="true"
								className={`pointer-events-none rounded-md bg-white dark:bg-black ${cell.isHiddenOnMobile ? "hidden sm:block" : ""}`}
							/>
						))}
						<div className="col-span-4 row-start-2 row-end-9 flex flex-col space-y-6 rounded-md bg-white dark:bg-black px-6 py-10 shadow-[0_0_0_1px_var(--border)] sm:col-start-2 sm:col-end-8 sm:px-8 lg:col-start-3 lg:col-end-7">
							<div className="space-y-1">
								<h1 className="text-2xl font-semibold tracking-[-0.04em]">
									Welcome back
								</h1>
								<p className="text-sm text-muted-foreground">
									AgentReacher is ready.
								</p>
							</div>

							{error && (
								<div className="rounded-md border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
									{error}
								</div>
							)}

							<div className="space-y-5">
								<button
									type="button"
									onClick={handleGoogleSignIn}
									className="w-full flex items-center justify-center gap-3 rounded-md border border-border bg-muted px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-accent cursor-pointer"
								>
									<svg
										className="w-5 h-5"
										viewBox="0 0 24 24"
										aria-hidden="true"
										focusable="false"
									>
										<path
											fill="#4285F4"
											d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
										/>
										<path
											fill="#34A853"
											d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
										/>
										<path
											fill="#FBBC05"
											d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
										/>
										<path
											fill="#EA4335"
											d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
										/>
									</svg>
									Continue with Google
								</button>

								<div className="relative">
									<div className="absolute inset-0 flex items-center">
										<div className="w-full h-px bg-border" />
									</div>
									<div className="relative flex justify-center text-sm">
										<span className="bg-background px-4 text-xs uppercase tracking-wider text-muted-foreground">
											or email
										</span>
									</div>
								</div>

								<form onSubmit={handleEmailLogin} className="space-y-4">
									<div>
										<label
											htmlFor={emailId}
											className="mb-2 block text-sm font-medium text-foreground"
										>
											Email
										</label>
										<div className="relative">
											<Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
											<input
												id={emailId}
												type="email"
												autoComplete="email"
												value={email}
												onChange={(e) => setEmail(e.target.value)}
												required
												className="w-full rounded-md border border-border bg-muted py-2.5 pl-11 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
												placeholder="name@company.com"
											/>
										</div>
									</div>

									<div>
										<label
											htmlFor={passwordId}
											className="mb-2 block text-sm font-medium text-foreground"
										>
											Password
										</label>
										<div className="relative">
											<Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
											<input
												id={passwordId}
												type="password"
												autoComplete="current-password"
												value={password}
												onChange={(e) => setPassword(e.target.value)}
												required
												className="w-full rounded-md border border-border bg-muted py-2.5 pl-11 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
												placeholder="Enter your password"
											/>
										</div>
										<div className="mt-2 text-right">
											<Link
												to="/forgot-password"
												className="text-sm font-medium text-primary transition-colors hover:text-foreground"
											>
												Forgot password?
											</Link>
										</div>
									</div>

									<button
										type="submit"
										disabled={loading}
										className="flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-colors disabled:cursor-not-allowed disabled:opacity-50"
									>
										{loading ? (
											<>
												<Loader2 className="h-4 w-4 animate-spin" />
												Signing in...
											</>
										) : (
											<>
												Sign in
												<ArrowRight className="h-4 w-4" />
											</>
										)}
									</button>
								</form>

								<p className="text-center text-sm text-muted-foreground">
									Don&apos;t have an account?{" "}
									<Link
										to="/signup"
										search={redirectParam ? { redirect: redirectParam } : {}}
										className="font-medium text-primary transition-colors hover:text-foreground"
									>
										Sign up
									</Link>
								</p>
							</div>
						</div>
					</main>
				</div>
			</div>
			<PublicFooter showCta={false} />
		</div>
	);
}
